import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { AxiosError } from "axios";
import * as microsoftTeams from "@microsoft/teams-js";
import {
    Flex, Button, SplitButton, MenuButton, Tooltip, Dropdown, Divider, PopupProps, TextArea
} from "@fluentui/react-northstar";
import {
    SaveIcon, TeamsMonochromeIcon, CheckmarkCircleIcon, OpenOutsideIcon, PlayIcon, LinkIcon,
    SpeakerPersonIcon, PollIcon, CanvasAddPageIcon, ShiftActivityIcon, ComposeIcon
} from "@fluentui/react-icons-northstar";
import find from "lodash/find";
import DatepickerWithLabel from "../components/pickers/datepickerWithLabel";
import InputWithLabel from "../components/common/inputWithLabel";
import Alert from "../components/common/alert";
import FieldWithLabel from "../components/common/fieldWithLabel";

import { useAuth } from "../contexts/authContext";
import useClearAuthIssue from "../common/useClearAuthIssue";
import { useApp } from "../contexts/appContext";
import { resolveProject, updateProject, openProject, changeProjectOwner } from "../services/dataService";
import { useCustomers, useInvalidateData, useProject, useProjects } from "../queries";
import { addDays } from "../common/dateOperations";
import { numberToString, stringToNumber } from "../common/numberOperations";
import useSaveShortcut from "../common/useSaveShortcut";
import {
    copyToClipboard, hasValue, projectStatusCodeToText, projectStatuses, projectStatusTextToCode
} from "../common/textOperations";
import styles from "../common.module.scss";
import IUpdateProject from "../interfaces/IUpdateProject";
import ICloseProjectData from "../interfaces/ICloseProjectData";
import { getSettings } from "../services/settingsService";
import ICustomer from "../interfaces/ICustomer";
import ISLATimer from "../interfaces/ISLATimer";
import SLATimer from "../components/slaTimer";
import IComment from "../interfaces/IComment";

const ProjectForm: React.FunctionComponent = () => {
    document.title = "Případ";
    const settings = getSettings();

    const { id } = useParams<{ id: string }>();
    const [errorMessage, setErrorMessage] = useState("");
    const [authIssue, setAuthIssue] = useState(true);
    const [showAddTooltip, setShowAddTooltip] = useState<boolean | undefined>();
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState<string | undefined>();
    const [start, setStart] = useState<Date | undefined>();
    const [end, setEnd] = useState(new Date());
    const [pendingUntil, setPendingUntil] = useState(addDays(new Date(), 7));
    const [remainingHours, setRemainingHours] = useState("0");
    const [status, setStatus] = useState(projectStatusCodeToText(100000000));
    const [editDescription, setEditDescription] = useState(false);
    const showPendingUntil = status === projectStatuses[2].text || status === projectStatuses[3].text;

    const { state: authState } = useAuth();
    const { accessToken: token } = authState;
    useClearAuthIssue(authIssue, () => { setErrorMessage(""); setAuthIssue(false); });
    const history = useHistory();
    const { state: appState, dispatch } = useApp();

    const { data: project, refetch: refetchProject } = useProject(id);
    const { data: customersData } = useCustomers();
    const { invalidateProjects } = useInvalidateData();

    // Keeps the project picker in the report form filled once the user gets there
    useProjects();

    const closedProject = project?.status === 5 || project?.status === 6;
    const statuses = closedProject ? projectStatuses.map(x => x.text) : projectStatuses.filter(x => !x.closedStatus).map(x => x.text);
    const customer = find(customersData, (x: ICustomer) => x.id === project?.customerId);
    const nda = customer?.nda ? customer.nda.replace(/\n/g, "<br />") : null;
    const fixBalance = (project?.baselinePlannedHours ?? 0) - (project?.reportedHours ?? 0);

    // The project may arrive after the first render, but the form must be filled only once
    // so that a background refresh never throws away what the user is typing
    const filledFromProject = useRef(false);

    useEffect(() => {
        if (!project || filledFromProject.current) { return; }
        filledFromProject.current = true;

        setSubject(project.subject);
        setDescription(project.description);
        setStart(project.startDate ? new Date(project.startDate) : new Date());
        setEnd(project.dueDate ? new Date(project.dueDate) : new Date());
        setPendingUntil(project.pendingUntil ? new Date(project.pendingUntil) : addDays(new Date(), 7));
        setRemainingHours(numberToString(project.remainingHours));
        setStatus(projectStatusCodeToText(project.status ?? 100000000));
    }, [project]);

    const toggleLoading = (loading: boolean) => {
        dispatch({ type: "SET_LOADER", show: loading });
    };

    const asyncCall = async (callback: any, navigateToList: boolean = true) => {
        setErrorMessage("");
        setAuthIssue(false);

        toggleLoading(true);
        try {
            await callback;
            toggleLoading(false);
            if (navigateToList) {
                invalidateProjects();
                history.push("/projects");
            }
        } catch (error) {
            const err: AxiosError = error as AxiosError;
            toggleLoading(false);
            if (err.response?.status === 401) {
                setErrorMessage("Přístupový token vypršel.");
                setAuthIssue(true);
            } else {
                setErrorMessage(err.message);
                console.log(err.response?.data);
            }
        }
    }

    const update = async () => {
        if (!token) { return; }

        const data: IUpdateProject = {
            statusCode: projectStatusTextToCode(status),
            subject: subject,
            description: description,
            startDate: start,
            dueDate: end,
            pendingUntil: pendingUntil,
            remainingHours: stringToNumber(remainingHours)
        };
        await asyncCall(updateProject(token, id, data, true));
    };

    const closeProject = async (data: ICloseProjectData) => {
        if (!token) { return; }
        await asyncCall(resolveProject(token, id, data));
    }

    const changeOwner = async (userId: string) => {
        if (!token) { return; }
        await asyncCall(changeProjectOwner(token, id, userId));
    }

    const activateProject = async () => {
        if (!token) { return; }
        await asyncCall(openProject(token, id));
    }

    const projectLink = () => {
        return import.meta.env.VITE_CRM_BASE_URL + `&pagetype=entityrecord&etn=incident&id=${id}`;
    };

    const modalResultClose = (data: any) => {
        if (!data.result) {
            return;
        }

        closeProject(data.result);
    };

    const showModalClose = () => {
        const followUpSla = project?.slaTimers?.find(x => x.name === "Follow Up By KPI");
        const followUpSlaActive = followUpSla ? !followUpSla.terminalStateReached : false;
        const args: string = `showSendEmail=${project?.isSupport ?? false}&contactEmail=${project?.contactEmail ?? ""}&slaActive=${followUpSlaActive}`;
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Potvrzení",
            size: {
                height: 330,
                width: 400
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/project/confirmresolve?${args}`
        };

        microsoftTeams.dialog.url.open(dialogInfo, modalResultClose);
    };

    const modalResultAssign = (data: any): void => {
        if (!data.result) {
            return;
        }
        changeOwner(data.result.id);
    };

    const showModalAssign = () => {
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Změna vlastníka případu",
            size: {
                height: 400,
                width: 400
            },
            url: import.meta.env.VITE_BASE_URL + "/modal/selectowner"
        };

        microsoftTeams.dialog.url.open(dialogInfo, modalResultAssign);
    };

    const modalResultComment = async (_: any) => {
        // The comment can move the SLA timers, the detail has to be read again
        filledFromProject.current = false;
        invalidateProjects();
        await asyncCall(refetchProject(), false);
    };

    const showModalComment = () => {
        const data: IComment = {
            supportProject: (project?.slaTimers && project?.slaTimers.length > 0) ?? false,
            engaged: project?.engaged ?? false,
            firstResponse: project?.firstResponse ?? false,
            followUp: project?.followUp ?? false
        };
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Interní komentář",
            size: {
                height: 340,
                width: 400
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/project/internalcomment/${id}?data=${JSON.stringify(data)}`
        };

        microsoftTeams.dialog.url.open(dialogInfo, modalResultComment);
    };

    const redirectToNewReport = () => {
        history.push(`/report/new?initProjectId=${id}`);
    }

    const openOrCopyLink = (link: string) => {
        if (settings.copyLinks) {
            copyToClipboard(link);
        } else {
            window.open(link);
        }
    };

    const formIsValid = () => {
        let isValid: boolean = true;
        if (showPendingUntil) {
            isValid = hasValue(pendingUntil)
        }

        return (
            isValid &&
            hasValue(start) &&
            hasValue(subject) &&
            hasValue(remainingHours)
        );
    };

    useSaveShortcut(update, !closedProject && formIsValid() && !appState.showLoader);

    if (!project) {
        return null;
    }

    return (
        <div>
            <Flex column gap="gap.medium" className={styles.formBody}>
                <Flex gap="gap.medium">
                    <Flex column gap="gap.smaller" fill>
                        <small>Status</small>
                        <Dropdown
                            fluid
                            items={statuses}
                            value={status}
                            disabled={closedProject}
                            onChange={(_: any, data: any) => setStatus(data.value)}
                        />
                    </Flex>
                    {showPendingUntil &&
                        <div className={styles.projectPendingUntil}>
                            <DatepickerWithLabel label="Pozastaveno do" date={pendingUntil} hideTime onChange={(date: Date) => setPendingUntil(date)} />
                        </div>
                    }
                </Flex>
                <InputWithLabel label="Název" value={subject} onChange={(_: any, data: any) => setSubject(data.value)} maxLength={328} disabled={closedProject} />
                {project.description && !editDescription &&
                    <div onClick={() => setEditDescription(true)}>
                        <FieldWithLabel value={project.description?.replace(/\n/g, "<br />")} label="Popis" />
                    </div>
                }
                {project.description && editDescription &&
                    <Flex column gap="gap.smaller">
                        <small>Popis</small>
                        <TextArea
                            value={description}
                            maxLength={2000}
                            rows={10}
                            fluid
                            resize="vertical"
                            onChange={(_: any, data: any) => setDescription(data.value)}
                        />
                    </Flex>
                }

                <div style={{ display: "flex", gap: 15, flexWrap: "wrap" }}>
                    <div className={styles.flex05}>
                        <DatepickerWithLabel label="Začátek" date={start} hideTime onChange={(date: Date) => setStart(date)} disabled={closedProject} allowManualInput={false} />
                    </div>
                    <div className={styles.flex05}>
                        <DatepickerWithLabel label="Konec" date={end} hideTime onChange={(date: Date) => setEnd(date)} disabled={closedProject} allowManualInput={false} />
                    </div>
                    <div className={styles.flex1}>
                        <InputWithLabel value={remainingHours} label="Zbývá odpracovat (h)" onChange={(_: any, data: any) => setRemainingHours(data.value)} disabled={closedProject} />
                    </div>
                </div>

                <Divider content="Související informace" />

                <Flex gap="gap.large" wrap>
                    <Flex gap="gap.medium" column style={{ marginBottom: 10, flexGrow: 1 }}>
                        <FieldWithLabel label="Vykázáno&nbsp;hodin" value={`${numberToString(project.reportedHours)} h`} />
                        <FieldWithLabel label="Výchozí&nbsp;plánovaná&nbsp;doba" value={`${numberToString(project.baselinePlannedHours ?? 0)} h`} />
                        {project?.projectMode === "FIX" &&
                            <div style={{ color: fixBalance < 0 ? "#da4f43" : undefined }}>
                                <FieldWithLabel label="Fix&nbsp;balance" value={`${numberToString(fixBalance)} h`} />
                            </div>
                        }
                        <FieldWithLabel label="Typ&nbsp;projektu" value={project.projectMode} />
                        {project.role &&
                            <FieldWithLabel label="Role" value={project.role} />
                        }
                    </Flex>
                    <Flex gap="gap.medium" column style={{ marginBottom: 10, flexGrow: 1 }}>
                        {project.parentProjectOwner &&
                            <FieldWithLabel label="Project&nbsp;manager" value={project.parentProjectOwner} />
                        }
                        {project.customerOwner &&
                            <FieldWithLabel label="Account&nbsp;manager" value={project.customerOwner} />
                        }
                        {project.language &&
                            <FieldWithLabel label="Jazyk&nbsp;projektu" value={project.language} />
                        }
                    </Flex>
                    {(project.contactName || project.contactPhone || project.contactEmail) &&
                        <Flex column style={{ marginBottom: 10, flexGrow: 1 }}>
                            <small>Kontakt</small>
                            <div>{project.contactName}</div>
                            <div>{project.contactPhone}</div>
                            <div>{project.contactEmail}</div>
                        </Flex>
                    }
                    <Flex gap="gap.medium" column style={{ marginBottom: 10, flexGrow: 1 }}>
                        {project.slaTimers?.map((t: ISLATimer) => <SLATimer key={t.name} {...t} />)}
                    </Flex>
                </Flex>

                {nda &&
                    <FieldWithLabel label="Specifika" value={nda} />
                }
                {customer?.aiRestriction &&
                    <Alert type="warning">AI Restriction viz. informace výše.</Alert>
                }
                {errorMessage && <Alert type="error" authIssue={authIssue}>{errorMessage}</Alert>}
            </Flex>

            <div className={styles.footer} style={{ background: appState.backgroundColor }}>
                <Flex gap="gap.smaller" className={styles.centerContainer}>
                    {settings.copyLinks ?
                        <Tooltip
                            trigger={<Button text iconOnly icon={<LinkIcon />} onClick={() => openOrCopyLink(projectLink())} />}
                            content="Kopírovat odkaz do CRM"
                        />
                        :
                        <Tooltip
                            trigger={<Button text iconOnly icon={<OpenOutsideIcon />} onClick={() => window.open(projectLink())} />}
                            content="Otevřít případ v CRM"
                        />
                    }
                    {project.teamsLink &&
                        <Tooltip
                            trigger={<Button text iconOnly icon={<TeamsMonochromeIcon />} onClick={() => openOrCopyLink(project.teamsLink ?? "")} />}
                            content={settings.copyLinks ? "Kopírovat odkaz do Teams kanálu" : "Otevřít Teams kanál"}
                        />
                    }
                    {project.opportunityNumber &&
                        <Tooltip
                            trigger={
                                <Button text iconOnly icon={<PollIcon />}
                                    onClick={() => openOrCopyLink(import.meta.env.VITE_PBI_OPPO_URL?.replace("[OpportunityNumber]", project.opportunityNumber ?? "") ?? "")}
                                />
                            }
                            content={settings.copyLinks ? "Kopírovat odkaz na report objednávky" : "Otevřít report objednávky"}
                        />
                    }
                    {!closedProject &&
                        <Tooltip
                            trigger={<Button text iconOnly icon={<SpeakerPersonIcon />} disabled={appState.showLoader} onClick={showModalAssign} />}
                            content="Změnit vlastníka"
                        />
                    }
                    {!closedProject &&
                        <Tooltip
                            content="Přidat..."
                            open={showAddTooltip}
                            trigger={
                                <MenuButton
                                    onOpenChange={(_: any, data: PopupProps | undefined) => setShowAddTooltip(data?.open ? false : undefined)}
                                    trigger={<Button text iconOnly icon={<ComposeIcon />} />}
                                    menu={[
                                        {
                                            key: "add-comment",
                                            icon: <CanvasAddPageIcon outline />,
                                            content: "Přidat interní komentář",
                                            onClick: showModalComment
                                        },
                                        {
                                            key: "add-report",
                                            icon: <ShiftActivityIcon outline />,
                                            content: "Přidat výkaz",
                                            onClick: redirectToNewReport
                                        }
                                    ]}
                                />
                            }
                        />
                    }
                    <Flex.Item push>
                        {closedProject ?
                            <Button content="Otevřít" primary icon={<PlayIcon />} disabled={appState.showLoader} loading={appState.showLoader} onClick={activateProject} />
                            :
                            <SplitButton
                                menu={[
                                    {
                                        key: "save",
                                        content: "Uložit",
                                        icon: <SaveIcon outline />,
                                        onClick: update
                                    },
                                    {
                                        key: "close-case",
                                        content: "Dokončit případ",
                                        icon: <CheckmarkCircleIcon outline />,
                                        onClick: showModalClose
                                    }
                                ]}
                                button={{
                                    content: "Uložit",
                                    icon: <SaveIcon outline />,
                                    loading: appState.showLoader,
                                    onClick: update
                                }}
                                disabled={!formIsValid() || appState.showLoader}
                                primary
                            />
                        }
                    </Flex.Item>
                </Flex>
            </div>
        </div>
    );
};

export default ProjectForm;
